import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Card,
    Switch,
    Tooltip
} from "@material-ui/core";

// @material-ui/icons
import { Edit } from "components/MaterialSymbol/MaterialIcons";

// Core components
import PageLayout from "layouts/PageLayout";
import ReactTable from "components/ReactTable/ReactTableV2.js";
import Button from "components/CustomButtons/Button.js";
import Modal from "components/Modal/Modal.js";
import Notification from "components/Notification/Notification.js";
import DeleteButton from "components/DeleteButton/DeleteButton.js";
import TextActionButton from "components/TextActionButton/TextActionButton";

// hooks & services
import useDealerId from "hooks/useDealerId";
import useCategoryLibrary from "hooks/useCategoryLibrary";
import useHomepageCategoriesSwr from "hooks/useHomepageCategoriesSwr";
import HomepageCategoriesService from "services/HomepageCategoriesService";
import CategoryLibraryDrawer from "./CategoryLibraryDrawer";
import HomepageCategoryForm from "./HomepageCategoryForm";
import HomepageCategoryFileUpload from "./HomepageCategoryFileUpload";

const useStyles = makeStyles((theme) => ({
    actionsCell: {
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(1)
    },
    uploadCell: {
        padding: '4px',
        minWidth: '150px'
    },
    compactTable: {
        "& td": {
            padding: "8px 10px !important",
        },
        "& th": {
            padding: "10px 10px !important",
        }
    },
    // New Row Highlight Animation
    "@keyframes flashRow": {
        "0%": { backgroundColor: "rgba(76, 175, 80, 0.2)" },
        "100%": { backgroundColor: "transparent" }
    },
    newRow: {
        animation: "$flashRow 3s ease-out forwards"
    }
}));

import VehicleService from "services/VehicleService";

export default function HomepageCategories() {
    const classes = useStyles();
    const dlid = useDealerId();
    const { categories, mutate, isLoading } = useHomepageCategoriesSwr();
    const { context } = useCategoryLibrary();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const [notification, setNotification] = useState(null);
    const [newlyAddedId, setNewlyAddedId] = useState(null);

    const openDrawer = () => setDrawerOpen(true);
    const closeDrawer = () => setDrawerOpen(false);

    const openEditModal = (cat) => setActiveCategory(cat);
    const closeEditModal = () => setActiveCategory(null);

    // Auto-clear highlight after animation
    useEffect(() => {
        if (newlyAddedId) {
            const timer = setTimeout(() => setNewlyAddedId(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [newlyAddedId]);

    const handleFormSuccess = (message) => {
        mutate();
        closeEditModal();
        setNotification({ color: "success", message });
    };

    /**
     * Handle Image Upload and DB Persistence (Full Path)
     */
    const handleQuickUpload = async (cat, file) => {
        try {
            if (file === null) {
                await HomepageCategoriesService.update(cat.id, {
                    image_path: null
                });
                mutate();
                setNotification({ color: "success", message: `Image removed.` });
                return;
            }

            const uploadRes = await HomepageCategoriesService.uploadImage(file, dlid);
            const imageUrl = uploadRes.data.fullUrl;

            await HomepageCategoriesService.update(cat.id, {
                image_path: imageUrl
            });

            mutate();
            setNotification({ color: "success", message: "Image updated." });
        } catch (error) {
            console.error('Upload persistence failed:', error);
            setNotification({ color: "danger", message: "Failed to save image." });
            mutate();
        }
    };

    const handleAddCategory = async (cat) => {
        console.log('[HomepageCategories] handleAddCategory triggered for:', cat.id);
        const tempId = Date.now();

        if (!context || !context.dlid) {
            console.warn('[HomepageCategories] Catalog context is not ready yet.');
            setNotification({ color: "warning", message: "Catalog is still synchronizing. Please wait a moment." });
            return;
        }

        // 1. Fetch subcategories for all locales to get the first one
        let subData = { en: null, es: null, pt: null, fr: null };
        try {
            const locales = ['en', 'es', 'pt', 'fr'];
            const subResults = await Promise.all(
                locales.map(loc => VehicleService.getSubcategories({
                    ...context,
                    categoryId: cat.id,
                    loc
                }))
            );

            locales.forEach((loc, index) => {
                const list = subResults[index] || [];
                if (list.length > 0) {
                    subData[loc] = list[0];
                }
            });
        } catch (e) {
            console.error('[HomepageCategories] Failed to resolve subcategories:', e);
            setNotification({ color: "danger", message: "Subcategory resolution failed." });
            return;
        }

        const subId = subData.en?.id || 0;
        if (!subId) {
            setNotification({ color: "danger", message: "No subcategories found for this selection." });
            return;
        }

        const optimisticCategory = {
            id: tempId,
            subcategoryId: subId,
            displayNameEn: cat.locales.en?.name || "",
            originalNameEn: cat.locales.en?.name || "",
            status: 1,
            sortOrder: -1,
            createdAt: new Date().toISOString()
        };

        const updatedCategories = [optimisticCategory, ...categories];
        setNewlyAddedId(tempId);

        try {
            mutate(updatedCategories, false);
            const payload = {
                dealer_id: dlid,
                category_id: cat.id,
                subcategory_id: subId,
                original_name_en: cat.locales.en?.name || "",
                original_name_es: cat.locales.es?.name || "",
                original_name_pt: cat.locales.pt?.name || "",
                original_name_fr: cat.locales.fr?.name || "",
                subcategory_name_en: subData.en?.name || "",
                subcategory_name_es: subData.es?.name || "",
                subcategory_name_pt: subData.pt?.name || "",
                subcategory_name_fr: subData.fr?.name || "",
                subcategory_slug_en: subData.en?.slug || "",
                subcategory_slug_es: subData.es?.slug || "",
                subcategory_slug_pt: subData.pt?.slug || "",
                subcategory_slug_fr: subData.fr?.slug || "",
                category_slug_en: cat.locales.en?.slug || "",
                category_slug_es: cat.locales.es?.slug || "",
                category_slug_pt: cat.locales.pt?.slug || "",
                category_slug_fr: cat.locales.fr?.slug || "",
                status: 1,
                sort_order: categories.length + 1
            };
            await HomepageCategoriesService.create(payload);
            mutate();
        } catch (error) {
            console.error('[HomepageCategories] Create failed:', error);
            mutate(categories);
            setNotification({ color: "danger", message: "Failed to add category." });
        }
    };

    const handleToggleStatus = async (category) => {
        const newStatus = category.status === 1 ? 0 : 1;
        const updatedCategories = categories.map(cat =>
            cat.id === category.id ? { ...cat, status: newStatus } : cat
        );

        try {
            mutate(updatedCategories, false);
            await HomepageCategoriesService.update(category.id, { status: newStatus });
            mutate();
        } catch (error) {
            mutate(categories);
            setNotification({ color: "danger", message: "Failed to update status." });
        }
    };

    const handleDelete = async (id) => {
        try {
            // Wait for backend confirmation
            await HomepageCategoriesService.remove(id);

            // Only after success, refresh data from server
            await mutate();
            setNotification({ color: "success", message: "Category permanently removed." });
        } catch (error) {
            console.error('Deletion failed:', error);
            setNotification({ color: "danger", message: "Failed to remove category. It may still be in use." });
        }
    };

    const columns = [
        {
            Header: "Image / Upload",
            id: "image",
            width: 200,
            Cell: ({ row }) => {
                const cat = row.original;
                return (
                    <div className={classes.uploadCell}>
                        <HomepageCategoryFileUpload
                            compact={true}
                            initialPreview={cat.imagePath}
                            onFileSelect={(file) => handleQuickUpload(cat, file)}
                        />
                    </div>
                );
            }
        },
        {
            Header: "Category Name",
            id: "category_name",
            Cell: ({ row }) => {
                const cat = row.original;
                const displayName = cat.displayNameEn || cat.originalNameEn;
                return (
                    <div className="text-bold">{displayName || 'Untitled Category'}</div>
                );
            }
        },
        {
            Header: "Created At",
            accessor: "createdAt",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString('en-US') : '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: 100,
            Cell: ({ row }) => (
                <Switch
                    checked={row.original.status === 1}
                    onChange={() => handleToggleStatus(row.original)}
                    color="primary"
                />
            )
        },
        {
            Header: () => <div style={{ textAlign: 'center', width: '100%' }}>Actions</div>,
            id: "actions",
            accessor: "id",
            sortable: false,
            filterable: false,
            width: 150,
            Cell: ({ row }) => (
                <div className={classes.actionsCell}>
                    <Tooltip title="Edit Details">
                        <Button
                            justIcon round simple
                            color="warning"
                            onClick={() => openEditModal(row.original)}
                        >
                            <Edit />
                        </Button>
                    </Tooltip>
                    <DeleteButton
                        title="Homepage Category"
                        handleClick={() => handleDelete(row.original.id)}
                    />
                </div>
            )
        }
    ];

    //
    const currentIds = categories.map(c => c.categoryId);

    return (
        <PageLayout
            title="Homepage Categories"
            showSelectDealer={true}
            customComponent={
                <TextActionButton onClick={openDrawer}>+ Add from Category Library</TextActionButton>
            }
        >
            {notification && (
                <Notification
                    open={!!notification}
                    color={notification.color}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}

            <CategoryLibraryDrawer
                open={drawerOpen}
                onClose={closeDrawer}
                onAdd={handleAddCategory}
                existingIds={currentIds}
            />

            <Modal
                open={!!activeCategory}
                modalTitle={activeCategory?.displayNameEn || activeCategory?.originalNameEn || "Refine Category"}
                handleClose={closeEditModal}
            >
                <HomepageCategoryForm
                    initialValues={activeCategory || {}}
                    onSuccess={handleFormSuccess}
                    onCancel={closeEditModal}
                />
            </Modal>

            <Card className={classes.compactTable}>
                <ReactTable
                    columns={columns}
                    data={categories}
                    showPagination={true}
                    defaultPageSize={10}
                    getTrProps={(state, rowInfo) => {
                        if (rowInfo && rowInfo.original && rowInfo.original.id === newlyAddedId) {
                            return { className: classes.newRow };
                        }
                        return {};
                    }}
                    LoadingComponent={() => isLoading ? <div style={{ padding: '40px', textAlign: 'center' }}>Synchronizing...</div> : null}
                />
            </Card>
        </PageLayout>
    );
}
